import React, { useEffect, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography,
  Grid,
} from "@mui/material";
import { ArrowBack, EventNote } from "@material-ui/icons";
import firebase from "../firebase";
import { palette } from "../theme";
import SessionsForm from "../Components/SessionsForm";
import PageContainer from "../Components/UI/PageContainer";
import PageHeader from "../Components/UI/PageHeader";

// Session editor with the selected kid's previous notes alongside, so the OT
// can reference past sessions while writing today's note — no page hopping.
export default function SessionsProfile() {
  const signedIn = global.config.uid !== "";
  const sessionID = new URLSearchParams(window.location.search).get("sessionID") || "";
  const [selectedKid, setSelectedKid] = useState("");
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!signedIn || selectedKid === "") {
      setHistory([]);
      return;
    }
    const db = firebase.firestore();
    const unsub = db
      .collection("Main")
      .doc(global.config.uid)
      .collection("Sessions")
      .where("Kid", "==", selectedKid)
      .onSnapshot((snapshot) => {
        const list = snapshot.docs
          .filter((doc) => doc.id !== sessionID)
          .map((doc) => ({
            id: doc.id,
            notes: doc.data().Notes,
            location: doc.data().Location,
            date: doc.data().Date ? doc.data().Date.toDate() : new Date(0),
          }));
        list.sort((a, b) => b.date - a.date);
        setHistory(list.slice(0, 10));
      });
    return unsub;
  }, [signedIn, selectedKid, sessionID]);

  if (!signedIn) return <Redirect to="/" />;

  return (
    <PageContainer>
      <Button
        component={Link}
        to="/sessions"
        startIcon={<ArrowBack />}
        sx={{ mb: 1, color: palette.textSecondary }}
      >
        Back to Sessions
      </Button>
      <PageHeader
        title={sessionID ? "Edit Session" : "Log a Session"}
        subtitle={
          sessionID
            ? "Update the details or notes for this session"
            : "Record what happened in today's session"
        }
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <SessionsForm onKidChange={setSelectedKid} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Previous Notes
              </Typography>
              {selectedKid === "" ? (
                <Box sx={{ textAlign: "center", py: 5, color: palette.textSecondary }}>
                  <EventNote style={{ fontSize: 36, opacity: 0.4 }} />
                  <Typography sx={{ mt: 1, fontWeight: 600 }}>
                    Pick a kid to see their past session notes here
                  </Typography>
                </Box>
              ) : history.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 5, color: palette.textSecondary }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    No previous sessions for this kid
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 560, overflowY: "auto", pr: 0.5 }}>
                  {history.map((s, i) => (
                    <Box key={s.id} sx={{ px: 0.5, py: 1.25 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {s.date.toLocaleDateString(undefined, {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </Typography>
                        {s.location && (
                          <Chip
                            size="small"
                            label={s.location}
                            sx={{
                              backgroundColor: palette.primarySoft,
                              color: palette.primaryDark,
                              height: 22,
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: palette.textSecondary, whiteSpace: "pre-line" }}
                      >
                        {s.notes || "No notes"}
                      </Typography>
                      {i < history.length - 1 && <Divider sx={{ mt: 1.25 }} />}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageContainer>
  );
}
